import { useDisclosure } from '@mantine/hooks';
import React, { useCallback, useMemo } from 'react';
import NeoLeoIcon from '../components/NeoLeo/NeoLeoIcon';
import NeoLeoSidebar from '../components/NeoLeo/NeoLeoSidebar';
import {
  createChatSession,
  openNeoLeo,
  saveShowIntro,
} from '../slices/LeoSlice';
import { useAppDispatch, useAppSelector } from '../slices/store';
import NeoLeoMobileIcon from '../components/NeoLeo/NeoLeoMobileIcon';
import NeoLeoMobileActionBar from '../components/NeoLeo/NeoLeoMobileActionBar';
import useDeviceType from './useDeviceType';

export interface UseNeoLeoOptions {
  notificationsLength?: number;
}

export interface UseNeoLeoResult {
  // State
  isNeoLeoOpen: boolean;
  neoLeoPanelOpened: boolean;
  agentId: string | undefined;
  isMobileActionBarOpen: boolean;

  // Actions
  openLeo: (
    createSession?: boolean,
    showIntro?: boolean,
  ) => Promise<string | undefined>;
  closeLeo: () => Promise<void>;
  handleNeoLeoToggle: () => void;
  openMobileActionBar: () => void;
  closeMobileActionBar: () => void;

  // Components to render
  NeoLeoIcon: React.ReactElement;
  NeoLeoSidebar: React.ReactElement;
  NeoLeoMobileIcon: React.ReactElement;
  NeoLeoMobileActionBar: React.ReactElement;
}

/**
 * Custom hook for managing Neo Leo chat functionality
 * Encapsulates all logic and UI components for Neo Leo integration
 *
 * @param options - Configuration options for the hook
 * @returns Object containing Neo Leo state, actions, and pre-configured components
 *
 * @example
 * ```tsx
 * const neoLeo = useNeoLeo({ notificationsLength: 2 });
 *
 * return (
 *   <div>
 *     {neoLeo.NeoLeoIcon}
 *     {neoLeo.NeoLeoSidebar}
 *   </div>
 * );
 * ```
 */
export const useNeoLeo = (options: UseNeoLeoOptions = {}): UseNeoLeoResult => {
  const { notificationsLength = 0 } = options;

  // Redux state and dispatch
  const dispatch = useAppDispatch();
  const { isMobile } = useDeviceType();
  const { authUserDetail, isNeoLeoOpen } = useAppSelector((state) => ({
    authUserDetail: state.auth.authUserDetail,
    isNeoLeoOpen: state.leo.isNeoLeoOpen,
  }));

  // Mantine disclosure hook for panel state
  const [
    neoLeoPanelOpened,
    { close: closeNeoLeoPanel, open: openNeoLeoPanel },
  ] = useDisclosure();

  // Mobile action bar state
  const [
    isMobileActionBarOpen,
    { close: closeMobileActionBar, open: openMobileActionBar },
  ] = useDisclosure();

  // Memoized agent ID
  const agentId = useMemo(() => {
    return authUserDetail?.id;
  }, [authUserDetail?.id]);

  // Open Leo with session creation
  const openLeo = useCallback(
    async (createSession: boolean = true, showIntro: boolean = true) => {
      if (!isNeoLeoOpen) {
        openNeoLeoPanel();
        dispatch(openNeoLeo(!isNeoLeoOpen));
      }
      dispatch(saveShowIntro(showIntro));

      if (createSession && agentId) {
        const sessionId = await dispatch(createChatSession(agentId));
        return sessionId;
      }

      return undefined;
    },
    [agentId, dispatch, isNeoLeoOpen, openNeoLeoPanel],
  );

  // Open Leo on mobile
  const openLeoMobile = useCallback(
    async (createSession: boolean = true, showIntro: boolean = true) => {
      if (!isNeoLeoOpen) {
        openMobileActionBar();
        dispatch(openNeoLeo(false));
      }
      dispatch(saveShowIntro(showIntro));

      if (createSession && agentId) {
        const sessionId = await dispatch(createChatSession(agentId));
        return sessionId;
      }

      return undefined;
    },
    [agentId, dispatch, isNeoLeoOpen, openMobileActionBar],
  );
  // Close Leo
  const closeLeo = useCallback(async () => {
    closeNeoLeoPanel();
    dispatch(openNeoLeo(false));
    dispatch(saveShowIntro(true));
  }, [closeNeoLeoPanel, dispatch]);

  // Toggle Leo open/close
  const handleNeoLeoToggle = useCallback(() => {
    if (isNeoLeoOpen) {
      closeLeo();
      return;
    }

    openLeo();
  }, [closeLeo, isNeoLeoOpen, openLeo]);

  // Toggle Leo open/close on mobile
  const handleNeoLeoToggleMobile = useCallback(() => {
    if (isMobile) {
      if (isMobileActionBarOpen) {
        closeMobileActionBar();
        return;
      }
      openLeoMobile();
    }
  }, [isMobile, isMobileActionBarOpen, openLeoMobile, closeMobileActionBar]);

  // Handle mobile action bar close
  const handleCloseMobileActionBar = useCallback(() => {
    closeMobileActionBar();
    dispatch(openNeoLeo(false));
  }, [closeMobileActionBar, dispatch]);

  // Pre-configured NeoLeoIcon component
  const NeoLeoIconElement = useMemo(
    () => (
      <NeoLeoIcon
        handleNeoLeoToggle={handleNeoLeoToggle}
        neoLeoPanelOpened={neoLeoPanelOpened}
        notificationsLength={notificationsLength}
      />
    ),
    [handleNeoLeoToggle, neoLeoPanelOpened, notificationsLength],
  );

  // Pre-configured NeoLeoMobileIcon component
  const NeoLeoMobileIconElement = useMemo(
    () => (
      <NeoLeoMobileIcon
        handleNeoLeoToggle={handleNeoLeoToggleMobile}
        isNeoLeoOpen={isMobileActionBarOpen}
      />
    ),
    [handleNeoLeoToggleMobile, isMobileActionBarOpen],
  );

  // Pre-configured NeoLeoMobileActionBar component
  const NeoLeoMobileActionBarElement = useMemo(
    () => (
      <NeoLeoMobileActionBar
        isOpen={isMobileActionBarOpen}
        onClose={handleCloseMobileActionBar}
      />
    ),
    [isMobileActionBarOpen, handleCloseMobileActionBar],
  );

  // Pre-configured NeoLeoSidebar component
  const NeoLeoSidebarElement = useMemo(
    () => <NeoLeoSidebar isNeoLeoOpen={isNeoLeoOpen} closeLeo={closeLeo} />,
    [isNeoLeoOpen, closeLeo],
  );

  return {
    // State
    isNeoLeoOpen,
    neoLeoPanelOpened,
    agentId,
    isMobileActionBarOpen,

    // Actions
    openLeo,
    closeLeo,
    handleNeoLeoToggle,
    openMobileActionBar,
    closeMobileActionBar,

    // Components
    NeoLeoIcon: NeoLeoIconElement,
    NeoLeoMobileIcon: NeoLeoMobileIconElement,
    NeoLeoSidebar: NeoLeoSidebarElement,
    NeoLeoMobileActionBar: NeoLeoMobileActionBarElement,
  };
};

export default useNeoLeo;
